import Image from 'next/image';
import { useMemo } from 'react';
import { NETWORKS } from 'src/common/types/network';

import useGetChainInfo from '@/hooks/useGetChainInfo';
import { getAddressScannerUrl, maskAddressValue } from '@/utils/format';

import { EventTriggerType, Trigger } from '../../domain';
import { TriggerExternalLinkDetail } from './components/TriggerExternalLinkDetail';
import { TriggerType } from './components/TriggerType';
import { TriggerValueDetail } from './components/TriggerValueDetail';

interface Props {
  trigger: Trigger;
  triggerType: EventTriggerType;
}

export const CompoundV3TriggerDetails = ({ trigger, triggerType }: Props) => {
  const { getChainIconUrl } = useGetChainInfo();

  const marketAddress = useMemo(() => {
    return trigger.context?.market_address || '';
  }, [trigger.context]);

  const positionType = useMemo(() => {
    const type = trigger.context?.position_type;
    return type === 'lending' ? 'Lending' : type === 'borrowing' ? 'Borrowing' : '';
  }, [trigger.context]);

  const networkInfo = useMemo(() => {
    const chainId = trigger.context?.market_chain_id;
    if (!chainId) return null;
    return NETWORKS.find((network) => network.chainId === chainId);
  }, [trigger.context]);

  const scannerUrl = useMemo(() => {
    if (!marketAddress || !networkInfo) return '';
    return getAddressScannerUrl('chainId', networkInfo.chainId, marketAddress) || '';
  }, [marketAddress, networkInfo]);

  const chainIconUrl = useMemo(() => {
    if (!networkInfo) return '';
    return getChainIconUrl(networkInfo.chainId) || '';
  }, [networkInfo, getChainIconUrl]);

  return (
    <div className="tw:flex tw:flex-row tw:gap-6">
      <TriggerType eventType={triggerType} />

      <TriggerExternalLinkDetail
        label="Market"
        value={
          <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center">
            {chainIconUrl && <Image src={chainIconUrl} width={12} height={12} alt={networkInfo?.name || 'chain'} />}
            <span>{maskAddressValue(marketAddress) ?? ''}</span>
          </div>
        }
        href={scannerUrl}
      />

      <TriggerValueDetail label="Position Type" value={positionType} />
    </div>
  );
};
